import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getPrivacyPolicy, getTermsAndConditions, AgreementResponse } from '@/controller/agreement';
import { sanitizeHTML } from '@/shared/utils/sanitize';

const CACHE_KEYS = {
    privacy: {
        data: 'pnc',
        timestamp: 'pnc_timestamp',
    },
    tnc: {
        data: 'tnc',
        timestamp: 'tnc_timestamp',
    },
};

const CACHE_DURATION = 6 * 60 * 60 * 1000;

export interface UseAgreementTermResult {
    content: string;
}

export const useAgreementTerm = (): UseAgreementTermResult => {
    const [content, setContent] = useState<string>('');
    const location = useLocation();

    // Determine type based on pathname
    const isPrivacy = location.pathname.includes('privacy-policy');
    const cacheKeys = isPrivacy ? CACHE_KEYS.privacy : CACHE_KEYS.tnc;

    useEffect(() => {
        window.scrollTo(0, 0);

        const localContent = localStorage.getItem(cacheKeys.data);
        const localTimestamp = localStorage.getItem(cacheKeys.timestamp);
        const now = Date.now();

        if (localContent && localTimestamp && now - parseInt(localTimestamp, 10) < CACHE_DURATION) {
            setContent(sanitizeHTML(localContent));
            return;
        }

        const controller = new AbortController();

        const fetchContent = async () => {
            try {
                const fetchFn = isPrivacy ? getPrivacyPolicy : getTermsAndConditions;
                const resp: AgreementResponse = await fetchFn(controller.signal);
                if (resp && resp.data && resp.data.content) {
                    const sanitizedContent = sanitizeHTML(resp.data.content);
                    localStorage.setItem(cacheKeys.data, sanitizedContent);
                    localStorage.setItem(cacheKeys.timestamp, now.toString());
                    setContent(sanitizedContent);
                }
            } catch (error) {
                // Request was either aborted or failed
            }
        };

        fetchContent();

        return () => {
            controller.abort();
        };
    }, [location.pathname, isPrivacy, cacheKeys]);

    return { content };
};
